import { z } from 'zod';
import { courseStatus, email, isoDateTime, markdown, mediumText, publishStatus, shortText, slug, uuid, } from './primitives.js';
// ------------------------------------------------------------------ course ---
export const courseSummary = z.object({
    id: uuid,
    slug,
    title: z.string(),
    summary: z.string(),
    thumbnailUrl: z.string().nullable(),
    status: courseStatus,
    sequentialUnlock: z.boolean(),
    sessionCount: z.number().int(),
    hasCapstone: z.boolean(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
    publishedAt: isoDateTime.nullable(),
});
/**
 * The authoring view of a course: the summary plus the two fields only an
 * author has any business with. `completionThresholdPct` decides when a session
 * counts as complete, so the console has to show the real number rather than
 * guess at it — and a learner is deliberately never told the bar they are being
 * measured against, which is why this is not folded into `courseSummary`.
 */
export const courseDetail = courseSummary.extend({
    descriptionMd: z.string(),
    completionThresholdPct: z.number().int(),
    /** Everyone is enrolled: every current account, and each new one as it is created. */
    autoEnroll: z.boolean(),
});
/** A learner's dashboard row: the course plus where they are in it. */
export const enrolledCourse = courseSummary.extend({
    progress: z.object({
        completedSessions: z.number().int(),
        totalSessions: z.number().int(),
        pct: z.number(),
    }),
    /** Where "continue where you left off" points. Null on a fresh enrollment. */
    resume: z
        .object({
        sessionId: uuid,
        sessionSlug: slug,
        sessionTitle: z.string(),
        orderIndex: z.number().int(),
        positionSec: z.number(),
    })
        .nullable(),
});
export const createCourseRequest = z.object({
    title: shortText.min(1),
    slug: slug.optional(),
    summary: mediumText.default(''),
    descriptionMd: markdown.default(''),
    sequentialUnlock: z.boolean().default(false),
    completionThresholdPct: z.number().int().min(1).max(100).default(90),
});
/**
 * Declared field by field rather than as `createCourseRequest.partial()`.
 * `.partial()` makes a key optional but leaves its `.default(...)` in place, so
 * parsing `{ title: 'x' }` would hand the handler `summary: ''`,
 * `completionThresholdPct: 90` and friends — and a PATCH that names only the
 * title would quietly blank the description and reset the completion
 * threshold. Absent must mean absent here.
 */
export const updateCourseRequest = z.object({
    title: shortText.min(1).optional(),
    slug: slug.optional(),
    summary: mediumText.optional(),
    descriptionMd: markdown.optional(),
    sequentialUnlock: z.boolean().optional(),
    completionThresholdPct: z.number().int().min(1).max(100).optional(),
    /** Switching it on enrols every existing account straight away. */
    autoEnroll: z.boolean().optional(),
    /**
     * Storage key from the generic file-upload endpoint (scope `course`). Absent
     * leaves the current thumbnail alone; an explicit `null` removes it.
     */
    thumbnailKey: z.string().max(1_024).nullable().optional(),
});
// ----------------------------------------------------------------- session ---
export const sessionComponentBadges = z.object({
    video: z.boolean(),
    deck: z.boolean(),
    notes: z.boolean(),
    interactive: z.boolean(),
    quiz: z.boolean(),
    assignment: z.boolean(),
});
export const sessionSummary = z.object({
    id: uuid,
    courseId: uuid,
    slug,
    title: z.string(),
    summary: z.string(),
    orderIndex: z.number().int(),
    estimatedDurationMin: z.number().int().nullable(),
    status: publishStatus,
    hasUnpublishedChanges: z.boolean(),
    publishedAt: isoDateTime.nullable(),
    components: sessionComponentBadges,
    /** Present for admins and for instructors looking at the console. */
    instructors: z
        .array(z.object({ userId: uuid, name: z.string(), email }))
        .default([]),
});
/** A session as a learner sees it in the course track. */
export const sessionTrackItem = sessionSummary.extend({
    progress: z.object({
        watchPct: z.number(),
        lastPositionSec: z.number(),
        completed: z.boolean(),
    }),
    /** False when sequentialUnlock is on and the previous session is incomplete. */
    unlocked: z.boolean(),
    lockedReason: z.string().nullable(),
    quizBest: z
        .object({ scorePct: z.number(), passed: z.boolean(), attempts: z.number().int() })
        .nullable(),
});
export const createSessionRequest = z.object({
    title: shortText.min(1),
    slug: slug.optional(),
    summary: mediumText.default(''),
    estimatedDurationMin: z.number().int().min(1).max(1440).nullable().default(null),
    /** Optional: create and assign in one step from the course settings page. */
    instructorUserIds: z.array(uuid).default([]),
    /** Trainers by address; anyone without an account yet gets one, and an invitation. */
    instructorEmails: z.array(email).max(200).default([]),
    /** Email the trainers assigned here. */
    notify: z.boolean().default(true),
});
export const updateSessionRequest = z.object({
    title: shortText.min(1).optional(),
    slug: slug.optional(),
    summary: mediumText.optional(),
    estimatedDurationMin: z.number().int().min(1).max(1440).nullable().optional(),
});
/**
 * Publishing names exactly what goes live, so the confirm dialog can restate it
 * and the audit entry can record it.
 */
export const publishRequest = z.object({
    publish: z.boolean(),
    /** Echoed back into the audit log; the UI sends what it showed the user. */
    acknowledgement: z.string().max(2_000).optional(),
});
export const publishPreflight = z.object({
    canPublish: z.boolean(),
    /** Hard stops. Publishing is refused while this is non-empty. */
    blockers: z.array(z.object({ code: z.string(), message: z.string() })),
    /** Soft notes — publish proceeds, but the confirm dialog shows them. */
    warnings: z.array(z.object({ code: z.string(), message: z.string() })),
    /** What the learner will gain access to, in plain words. */
    willGoLive: z.array(z.string()),
    /** True when the course itself is unpublished, so the session stays dark. */
    courseUnpublished: z.boolean(),
});
// -------------------------------------------------------------- assignment ---
export const assignInstructorsRequest = z.object({
    userIds: z.array(uuid),
    /** Trainers by address; anyone without an account yet gets one, and an invitation. */
    emails: z.array(email).max(200).default([]),
    /** Email the trainers this adds. Removals are never emailed. */
    notify: z.boolean().default(true),
});
export const assignCourseAdminsRequest = z.object({
    userIds: z.array(uuid),
    /** Course admins by address; anyone without an account yet gets one, and an invitation. */
    emails: z.array(email).max(200).default([]),
    /** Email the course admins this adds. Removals are never emailed. */
    notify: z.boolean().default(true),
});
// ------------------------------------------------------------------ people ---
/**
 * The people search behind the course console's pickers. Course-scoped so a
 * course admin can use it too (the platform user list is admin-only), and
 * deliberately thin: who someone is, not what they do on the platform.
 */
export const coursePeopleQuery = z.object({
    q: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(50),
});
export const coursePerson = z.object({
    id: uuid,
    name: z.string(),
    email,
    globalRole: z.enum(['admin', 'member']),
    isActive: z.boolean(),
    hasLoggedIn: z.boolean(),
});
export const coursePeopleResponse = z.object({
    items: z.array(coursePerson),
    total: z.number().int(),
    /** Only addresses at this domain can be added by email. */
    allowedEmailDomain: z.string(),
});
// -------------------------------------------------------------- enrollment ---
export const enrollmentRow = z.object({
    userId: uuid,
    email,
    name: z.string(),
    globalRole: z.enum(['admin', 'member']),
    enrolledAt: isoDateTime,
    progressPct: z.number(),
    completedSessions: z.number().int(),
    lastActiveAt: isoDateTime.nullable(),
});
export const enrollRequest = z.object({
    /** Existing users by id, plus raw emails for people who have never logged in. */
    userIds: z.array(uuid).default([]),
    emails: z.array(email).default([]),
    /**
     * Email the people this enrols (an invite for a new account). For a course
     * that is not published yet, the email waits for the publish.
     */
    notify: z.boolean().default(true),
});
export const enrollResult = z.object({
    enrolled: z.number().int(),
    alreadyEnrolled: z.number().int(),
    invitedNewUsers: z.number().int(),
    rejected: z.array(z.object({ value: z.string(), reason: z.string() })),
    /** Emails on their way, including ones waiting for the course to publish. */
    emailsQueued: z.number().int(),
});
/** What "enroll everyone on the platform" would cover: active accounts only. */
export const enrollEveryoneSummary = z.object({
    activeAccounts: z.number().int(),
    notEnrolled: z.number().int(),
});
/**
 * What an uploaded or pasted roster would do, worked out without doing it — so
 * an admin sees the format was understood (and who is new) before anyone is
 * enrolled or emailed.
 */
export const enrollCsvPreview = z.object({
    /** False when the file cannot be used at all; `formatError` says why. */
    formatOk: z.boolean(),
    formatError: z.string().nullable(),
    /** The file's own column names that were read, null when absent. */
    emailColumn: z.string().nullable(),
    nameColumn: z.string().nullable(),
    hasHeader: z.boolean(),
    /** Data rows in the file, blank lines excluded. */
    totalRows: z.number().int(),
    /** Distinct addresses that would be enrolled. */
    valid: z.number().int(),
    duplicatesInFile: z.number().int(),
    alreadyEnrolled: z.number().int(),
    existingAccounts: z.number().int(),
    newAccounts: z.number().int(),
    /** Rows that cannot be enrolled (bad address, wrong domain). */
    rejected: z.array(z.object({ value: z.string(), reason: z.string() })),
    sample: z.array(z.object({
        name: z.string().nullable(),
        email: z.string(),
        status: z.enum(['new', 'existing', 'enrolled']),
    })),
});
//# sourceMappingURL=courses.js.map